/**
 * Adapter para respostas da API de Monitoramento
 * 
 * Centraliza validação e extração de dados das respostas do backend C#,
 * isolando a lógica de adaptação do novo formato (envelope result.data).
 * 
 * @module MonitorResponseAdapter
 */

import { MonitorCommandResponse } from '../types/monitoring';
import { sendToTerminal } from '../../terminalLogger';

// Import lazy do debug helper para evitar circular dependency
let debugHelper: any = null;
function getDebugHelper() {
  if (!debugHelper) {
    try {
      debugHelper = require('./monitorDebugHelper').MonitorDebugHelper;
    } catch {
      debugHelper = { logValidation: () => {}, isVerbose: () => false };
    }
  }
  return debugHelper;
}

/**
 * Classe adapter para processar respostas do monitor
 * 
 * Responsabilidades:
 * - Validar formato de resposta
 * - Extrair dados com type safety
 * - Tratar erros de forma consistente
 * - Identificar ACKs de progresso
 */
export class MonitorResponseAdapter {
  /**
   * Valida resposta e extrai dados com type safety
   * 
   * Realiza validação em 3 níveis:
   * 1. Status HTTP-like (ok vs erro)
   * 2. Sucesso do comando (result.sucesso)
   * 3. Presença de dados (result.data não-null, exceto para 'parar')
   * 
   * @param response - Resposta do backend
   * @param comando - Nome do comando executado (para logs)
   * @returns Dados extraídos do envelope
   * @throws Error se validação falhar
   */
  static getData<T>(response: MonitorCommandResponse<T>, comando: string): T {
    const debug = getDebugHelper();
    
    // Validação 1: Status HTTP-like
    debug.logValidation?.(comando, 'status', response.status === 'ok', { 
      status: response.status 
    });
    
    if (response.status !== 'ok') {
      const error = (response as any).message || 'Erro desconhecido';
      sendToTerminal(`❌ [MONITOR:${comando.toUpperCase()}] Status não-ok`, { error });
      throw new Error(error);
    }
    
    // Validação 2: Sucesso do comando
    debug.logValidation?.(comando, 'sucesso', response.result.sucesso === true, {
      sucesso: response.result.sucesso
    });
    
    if (!response.result.sucesso) {
      const error = response.result.mensagem || 'Comando falhou';
      sendToTerminal(`❌ [MONITOR:${comando.toUpperCase()}] Comando falhou`, { error });
      throw new Error(error);
    }
    
    // Validação 3: Data presente (exceto para 'parar')
    const hasData = response.result.data !== null;
    debug.logValidation?.(comando, 'data', hasData || comando === 'parar', {
      hasData,
      comandoEsperaNull: comando === 'parar'
    });
    
    if (comando !== 'parar' && response.result.data === null) {
      const error = 'Dados ausentes na resposta';
      sendToTerminal(`❌ [MONITOR:${comando.toUpperCase()}] Data null`, {});
      throw new Error(error);
    }
    
    // Log de sucesso em modo verbose
    if (debug.isVerbose?.()) {
      const dataKeys = response.result.data ? Object.keys(response.result.data) : [];
      debug.logSuccess?.(comando, dataKeys);
    }
    
    return response.result.data as T;
  }
  
  /**
   * Verifica sucesso sem lançar exceção
   * 
   * Útil para validações não-críticas onde queremos apenas verificar
   * se o comando foi bem-sucedido sem interromper o fluxo.
   * 
   * @param response - Resposta do backend
   * @returns true se status ok E sucesso true
   */
  static isSuccess(response: MonitorCommandResponse): boolean {
    return response.status === 'ok' && response.result.sucesso === true;
  }
  
  /**
   * Obtém mensagem de erro de forma segura
   * 
   * Tenta extrair mensagem de erro da resposta, com fallbacks:
   * 1. result.mensagem (padrão novo)
   * 2. message (fallback antigo)
   * 3. "Erro desconhecido" (default)
   * 
   * @param response - Resposta do backend
   * @returns Mensagem de erro legível
   */
  static getErrorMessage(response: MonitorCommandResponse): string {
    return response.result.mensagem || (response as any).message || 'Erro desconhecido';
  }
  
  /**
   * Verifica se é ACK de progresso
   * 
   * ACKs de progresso são enviados antes da resposta final para:
   * - iniciar: message = "monitorando"
   * - sync: message = "coletando"
   * 
   * @param response - Resposta potencialmente sendo ACK
   * @returns true se for ACK de progresso válido
   */
  static isProgressAck(response: any): boolean {
    return response?.type === 'progress' && 
           (response?.message === 'monitorando' || response?.message === 'coletando');
  }
}

